<template>
  <div v-if="hasAnyDropdown" class="ctx-toolbar" role="toolbar" aria-label="Contexte de navigation">
    <!-- Site -->
    <div v-if="showSite" class="ctx-item">
      <template v-if="overrideSite">
        <button class="ctx-badge ctx-badge--overridden" title="Filtré par lien — cliquer pour revenir au filtre global"
          @click="clearSiteOverride">
          <font-awesome-icon icon="building" class="ctx-badge__icon" />
          <span class="ctx-badge__text">{{ overrideSite.name }}</span>
        </button>
      </template>
      <template v-else-if="!store.pinnedSite">
        <select :value="store.selectedSite?.id ?? ''" :disabled="store.loadingSites"
          :class="['ctx-select', { 'ctx-select--highlight': props.highlight?.includes('site') }]"
          @change="onSiteChange">
          <option value="" class="ctx-placeholder">Centre</option>
          <option v-for="s in store.sites" :key="s.id" :value="s.id">{{ s.name }}</option>
        </select>
        <button class="ctx-pin-btn" title="Épingler le centre" :disabled="!store.selectedSite"
          @click="store.pinnedSite = true">
          <font-awesome-icon icon="thumbtack" />
        </button>
      </template>
      <template v-else>
        <button class="ctx-badge" title="Désépingler le centre" @click="store.pinnedSite = false">
          <font-awesome-icon icon="building" class="ctx-badge__icon" />
          <span class="ctx-badge__text">{{ store.selectedSite?.name ?? 'Tous' }}</span>
        </button>
      </template>
    </div>

    <span v-if="showSepBeforeProgram" class="ctx-sep" aria-hidden="true" />

    <!-- Program -->
    <div v-if="showProgram" class="ctx-item">
      <template v-if="overrideProgram">
        <button class="ctx-badge ctx-badge--overridden" title="Filtré par lien — cliquer pour revenir au filtre global"
          @click="clearProgramOverride">
          <font-awesome-icon icon="box-archive" class="ctx-badge__icon" />
          <span class="ctx-badge__text">{{ overrideProgram.title }}</span>
        </button>
      </template>
      <template v-else-if="!store.pinnedProgram">
        <select :value="store.selectedProgram?.id ?? ''" :disabled="store.loadingPrograms"
          :class="['ctx-select', { 'ctx-select--highlight': props.highlight?.includes('program') }]"
          @change="onProgramChange">
          <option value="" class="ctx-placeholder">Programme</option>
          <optgroup label="Groupes">
            <option v-for="p in store.filteredPrograms.filter(p => !p.isIndividual)" :key="p.id" :value="p.id">
              {{ p.title }}
            </option>
          </optgroup>
          <optgroup label="Individualisés">
            <option v-for="p in store.filteredPrograms.filter(p => p.isIndividual)" :key="p.id" :value="p.id">
              {{ p.title }}
            </option>
          </optgroup>
        </select>
        <button class="ctx-pin-btn" title="Épingler le programme" :disabled="!store.selectedProgram"
          @click="store.pinnedProgram = true">
          <font-awesome-icon icon="thumbtack" />
        </button>
      </template>
      <template v-else>
        <button class="ctx-badge" title="Désépingler le programme" @click="store.pinnedProgram = false">
          <font-awesome-icon icon="box-archive" class="ctx-badge__icon" />
          <span class="ctx-badge__text">
            {{ store.selectedProgram?.title ?? 'Tous' }}
          </span>
        </button>
      </template>
    </div>

    <span v-if="showSepBeforeGroup" class="ctx-sep" aria-hidden="true" />

    <!-- Group -->
    <div v-if="showGroup" class="ctx-item">
      <template v-if="overrideGroup">
        <button class="ctx-badge ctx-badge--overridden" title="Filtré par lien — cliquer pour revenir au filtre global"
          @click="clearGroupOverride">
          <font-awesome-icon icon="users" class="ctx-badge__icon" />
          <span class="ctx-badge__text">{{ overrideGroup.name }}</span>
        </button>
      </template>
      <template v-else-if="!store.pinnedGroup">
        <select :value="store.selectedGroup?.id ?? ''" :disabled="store.loadingGroups"
          :class="['ctx-select', { 'ctx-select--highlight': props.highlight?.includes('group') }]"
          @change="onGroupChange">
          <option value="" class="ctx-placeholder">Groupe</option>
          <template v-if="store.groupStatusFilter === 'all'">
            <optgroup label="Confirmés">
              <option v-for="g in store.filteredGroups.filter(g => g.status === GroupStatus.Confirmed)" :key="g.id"
                :value="g.id">
                {{ g.name }}
              </option>
            </optgroup>
            <optgroup label="Non confirmés">
              <option v-for="g in store.filteredGroups.filter(g => g.status === GroupStatus.Unconfirmed)" :key="g.id"
                :value="g.id">
                {{ g.name }}
              </option>
            </optgroup>
            <optgroup label="Projetés">
              <option v-for="g in store.filteredGroups.filter(g => g.status === GroupStatus.Projected)" :key="g.id"
                :value="g.id">
                {{ g.name }}
              </option>
            </optgroup>
          </template>
          <template v-else>
            <option v-for="g in store.filteredGroups" :key="g.id" :value="g.id">
              {{ g.name }}{{ store.groupStatusFilter === GroupStatus.Unconfirmed ? ' (non confirmé)' :
                store.groupStatusFilter === GroupStatus.Projected ? ' (projeté)' : '' }}
            </option>
          </template>
        </select>
        <button class="ctx-pin-btn" title="Épingler le groupe" :disabled="!store.selectedGroup"
          @click="store.pinnedGroup = true">
          <font-awesome-icon icon="thumbtack" />
        </button>
      </template>
      <template v-else>
        <button class="ctx-badge" title="Désépingler le groupe" @click="store.pinnedGroup = false">
          <font-awesome-icon icon="users" class="ctx-badge__icon" />
          <span class="ctx-badge__text">
            {{ store.selectedGroup?.name ?? 'Tous' }}{{ store.selectedGroup && (store.groupStatusFilter ===
              GroupStatus.Unconfirmed || (store.groupStatusFilter === 'all' && store.selectedGroup.status ===
                GroupStatus.Unconfirmed)) ? ' (non confirmé)' : store.selectedGroup && (store.groupStatusFilter ===
                  GroupStatus.Projected || (store.groupStatusFilter === 'all' && store.selectedGroup.status ===
                    GroupStatus.Projected)) ? ' (projeté)' : '' }}
          </span>
        </button>
      </template>
    </div>

    <span v-if="showSepBeforeTeacher" class="ctx-sep" aria-hidden="true" />

    <!-- Teacher -->
    <div v-if="showTeacher" class="ctx-item">
      <template v-if="!store.pinnedTeacher">
        <select :value="store.selectedTeacher?.id ?? ''" :disabled="store.loadingTeachers"
          :class="['ctx-select', { 'ctx-select--highlight': props.highlight?.includes('teacher') }]"
          @change="onTeacherChange">
          <option value="" class="ctx-placeholder">Enseignant</option>
          <option v-for="t in store.filteredTeachers" :key="t.id" :value="t.id">
            {{ t.user.firstName }} {{ t.user.lastName }}
          </option>
        </select>
        <button class="ctx-pin-btn" title="Épingler l'enseignant" :disabled="!store.selectedTeacher"
          @click="store.pinnedTeacher = true">
          <font-awesome-icon icon="thumbtack" />
        </button>
      </template>
      <template v-else>
        <button class="ctx-badge" title="Désépingler l'enseignant" @click="store.pinnedTeacher = false">
          <font-awesome-icon icon="chalkboard-user" class="ctx-badge__icon" />
          <span class="ctx-badge__text">
            {{ store.selectedTeacher ? `${store.selectedTeacher.user.firstName} ${store.selectedTeacher.user.lastName}`
              : 'Tous' }}
          </span>
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useContextToolbarStore } from '@/stores/context-toolbar-store';
import { GroupStatus } from '@/sdk/model/group';
import type { ContextDropdown } from '@/routes';

const props = defineProps<{ highlight?: ContextDropdown[] }>();

const store = useContextToolbarStore();
const route = useRoute();
const router = useRouter();

// Reset the group status filter to the route's default whenever the active route changes.
watch(
  () => route.name,
  () => {
    const meta = route.meta as { groupStatusDefault?: 'confirmed' | 'unconfirmed' | 'projected' | 'all' };
    const default_ = meta.groupStatusDefault ?? 'confirmed';
    const newFilter = default_ === 'all'
      ? 'all'
      : default_ === 'unconfirmed'
        ? GroupStatus.Unconfirmed
        : default_ === 'projected'
          ? GroupStatus.Projected
          : GroupStatus.Confirmed;
    store.switchGroupStatusFilter(newFilter);
  },
  { immediate: true }
);

const visibleDropdowns = computed<ContextDropdown[]>(() =>
  (route.meta.contextToolbar as ContextDropdown[] | undefined) ?? ['site', 'program', 'group', 'teacher']
);

const overrideSiteId = computed<string | null>(() => {
  if (!route.meta.overrideSiteFromQuery) return null;
  const v = route.query.siteId;
  return typeof v === 'string' && v ? v : null;
});

const overrideSite = computed(() =>
  overrideSiteId.value ? store.sites.find(s => s.id === overrideSiteId.value) ?? null : null
);

const overrideProgramId = computed<string | null>(() => {
  if (!route.meta.overrideProgramFromQuery) return null;
  const v = route.query.programId;
  return typeof v === 'string' && v ? v : null;
});

const overrideProgram = computed(() =>
  overrideProgramId.value ? store.programs.find(p => p.id === overrideProgramId.value) ?? null : null
);

const overrideGroupId = computed<string | null>(() => {
  if (!route.meta.overrideGroupFromQuery) return null;
  const v = route.query.groupId;
  return typeof v === 'string' && v ? v : null;
});

const overrideGroup = computed(() =>
  overrideGroupId.value ? store.groups.find(g => g.id === overrideGroupId.value) ?? null : null
);

const showSite = computed(() => visibleDropdowns.value.includes('site'));
const showProgram = computed(() => visibleDropdowns.value.includes('program'));
const showGroup = computed(() => visibleDropdowns.value.includes('group'));
const showTeacher = computed(() => visibleDropdowns.value.includes('teacher'));

const hasAnyDropdown = computed(() => showSite.value || showProgram.value || showGroup.value || showTeacher.value);

const showSepBeforeProgram = computed(() => showProgram.value && showSite.value);
const showSepBeforeGroup = computed(() => showGroup.value && (showSite.value || showProgram.value));
const showSepBeforeTeacher = computed(() => showTeacher.value && (showSite.value || showProgram.value || showGroup.value));

onMounted(async () => {
  if (!store.initialized) {
    await store.initialize();
  }
});

function onSiteChange(e: Event) {
  const id = (e.target as HTMLSelectElement).value;
  store.selectedSite = store.sites.find(s => s.id === id) ?? null;
}

function onProgramChange(e: Event) {
  const id = (e.target as HTMLSelectElement).value;
  store.selectedProgram = store.filteredPrograms.find(p => p.id === id) ?? null;
}

function onGroupChange(e: Event) {
  const id = (e.target as HTMLSelectElement).value;
  store.selectedGroup = store.filteredGroups.find(g => g.id === id) ?? null;
}

function onTeacherChange(e: Event) {
  const id = (e.target as HTMLSelectElement).value;
  store.selectedTeacher = store.filteredTeachers.find(t => t.id === id) ?? null;
}

function clearSiteOverride() {
  const { siteId: _ignored, ...rest } = route.query;
  router.replace({ query: rest });
}

function clearProgramOverride() {
  const { programId: _ignored, ...rest } = route.query;
  router.replace({ query: rest });
}

function clearGroupOverride() {
  const { groupId: _ignored, ...rest } = route.query;
  router.replace({ query: rest });
}
</script>

<style scoped>
.ctx-toolbar {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0 10px;
  height: 36px;
  background: transparent;
  pointer-events: auto;
}

.ctx-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
}

.ctx-select {
  height: 22px;
  padding: 0 6px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.75);
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 4px;
  outline: none;
  cursor: pointer;
  min-width: 110px;
  max-width: 160px;
  appearance: none;
}

.ctx-select:focus {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(0, 0, 0, 0.07);
}

.ctx-select:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.ctx-select option {
  background: #fff;
  color: rgba(0, 0, 0, 0.75);
}

.ctx-select option.ctx-placeholder,
.ctx-placeholder {
  font-style: italic;
  color: rgba(0, 0, 0, 0.4);
}

.ctx-pin-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  font-size: 10px;
  color: rgba(0, 0, 0, 0.35);
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
}

.ctx-pin-btn:hover:not(:disabled) {
  color: rgba(0, 0, 0, 0.75);
  background: rgba(0, 0, 0, 0.07);
}

.ctx-pin-btn:disabled {
  opacity: 0.25;
  cursor: not-allowed;
}

.ctx-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 22px;
  padding: 0 8px;
  font-size: 12px;
  font-weight: 500;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.1);
  border: 1px solid rgba(var(--v-theme-primary), 0.25);
  border-radius: 11px;
  white-space: nowrap;
  max-width: 180px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.ctx-badge:hover {
  background: rgba(var(--v-theme-primary), 0.18);
  border-color: rgba(var(--v-theme-primary), 0.45);
}

.ctx-badge--overridden {
  background: rgba(var(--v-theme-primary), 0.22);
  border-color: transparent;
  font-style: italic;
}

.ctx-badge--overridden:hover {
  background: rgba(var(--v-theme-primary), 0.32);
  border-color: transparent;
}

.ctx-badge__icon {
  font-size: 10px;
  flex-shrink: 0;
}

.ctx-badge__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}


.ctx-select--highlight {
  animation: ctx-pulse 1.2s ease-in-out infinite;
}

@keyframes ctx-pulse {

  0%,
  100% {
    border-color: rgba(0, 0, 0, 0.18);
    box-shadow: none;
    background: rgba(0, 0, 0, 0.05);
  }

  50% {
    border-color: rgb(var(--v-theme-primary));
    box-shadow: 0 0 0 4px rgba(var(--v-theme-primary), 0.35), 0 0 10px rgba(var(--v-theme-primary), 0.25);
    background: rgba(var(--v-theme-primary), 0.1);
  }
}

.ctx-sep {
  display: block;
  width: 1px;
  height: 18px;
  background: rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}
</style>
